import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Input } from "reactstrap";
import { Download, Filter as FilterIcon } from "react-feather";
import moment from "moment";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import "./demo-ops.scss";
import { FilterModal, LeadsTable } from "./DemoOpsLeadsView";
import { LiveDemoTable } from "./LiveDemoTable";
import {
  filterLiveDemoLeads,
  isAsiaKolkataTimezone,
  isIndiaCountryCode,
  LIVE_DEMO_STATUS_OPTIONS,
  type LiveDemoAeFilter,
  type LiveDemoFilters,
  type LiveDemoSortDir,
  type LiveDemoStatusFilter,
} from "./liveDemoUtils";
import type { DemoLead, DemoOpsTabKey, FilterOnField, LeadSaveTouched } from "./demoOps.lib";
import {
  getDemoOpsSession,
  getDisplayDemoStatus,
  leadDisplayName,
  loadDemoOpsLeadsPage,
  persistLeadWithAudit,
  resolveUpdateAgentId,
  teacherDisplayName,
} from "./demoOps.lib";
import {
  getDemoOpsAgents,
  getDemoOpsAgentStatus,
  postDemoOpsAgentAvailability,
  postDemoOpsAgentShiftStatus,
} from "../../../Api/apiUtils";
import { hasEmailAccess, useEmailAccessConfig } from "../../../utils/emailAccessConfig";
import { isOutSourcingUser, isSalesRestrictedUser } from "../../../utils/permissions";
import { MixpanelEvents, trackEvent } from "../../../utils/mixpanelTracking";
import {
  Badge,
  Button,
  Card,
  Field,
  Page,
  PageHeader,
  Select,
  TextInput,
  Toolbar,
} from "../../../ui";
import { Tabs } from "./ui-pending/Tabs";
import { Pager } from "./ui-pending/Pager";

function tabFromUnknown(v: unknown): DemoOpsTabKey | undefined {
  if (v === "myleads" || v === "allleads" || v === "livedemo") return v;
  return undefined;
}

const PAGE_SIZE = 30;
const AGENT_FILTER_PAGE_SIZE = 1000;

function ymd(d: moment.Moment) {
  return d.format("YYYY-MM-DD");
}

function escapeCsv(cell: unknown): string {
  const s = `${cell ?? ""}`.replace(/"/g, '""');
  if (/[,"\n]/.test(s)) return `"${s}"`;
  return s;
}

type ShiftRow = { id: string; status: string; day: string; start: string; end: string };

// A next-day slot that starts within this many minutes of the chosen one ending joins it
const NEXT_DAY_JOIN_MINUTES = 120;
const WEEK_DAYS = [
  "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",
];

function toMinutes(t: string): number {
  const [h, m] = t.split(":");
  return Number(h) * 60 + Number(m);
}

function hm(t?: string): string {
  return String(t ?? "").slice(0, 5);
}

function findNextDayJoin(rows: ShiftRow[], chosen: ShiftRow): ShiftRow | undefined {
  const nextDay = WEEK_DAYS[(WEEK_DAYS.indexOf(chosen.day) + 1) % 7];
  return rows
    .filter((r) => r.day === nextDay && r.id !== chosen.id)
    .map((r) => ({ r, gap: 1440 - toMinutes(chosen.end) + toMinutes(r.start) }))
    .filter((x) => x.gap >= 0 && x.gap <= NEXT_DAY_JOIN_MINUTES)
    .sort((x, y) => x.gap - y.gap)[0]?.r;
}

type PresetKey = "today" | "week" | "custom";

const MyLeadsTabBody: React.FC<{
  visible: boolean;
  pollingEnabled: boolean;
}> = ({ visible, pollingEnabled }) => {
  const navigate = useNavigate();
  const session = getDemoOpsSession();
  const [leads, setLeads] = useState<DemoLead[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterStr, setFilterStr] = useState("");
  const [filterOn, setFilterOn] = useState<FilterOnField>("");

  const load = useCallback(async () => {
    if (!session.agentId) {
      toast.error("Missing agent id — check login profile.");
      return;
    }
    try {
      setLoading(true);
      const data = await loadDemoOpsLeadsPage({
        agentId: session.agentId,
        page,
        size: 500,
        filter: filterStr,
        filterOn,
      });
      setLeads(data.content ?? []);
      setTotalPages(Math.max(1, data.totalPages ?? 1));
    } catch (e) {
      console.error(e);
      toast.error("Could not load My Leads.");
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, [session.agentId, page, filterStr, filterOn]);

  useEffect(() => {
    if (!visible) return;
    void load();
  }, [visible, load]);

  useEffect(() => {
    if (!visible || !pollingEnabled || !session.agentId) return;
    const id = window.setInterval(() => {
      void load();
    }, 10 * 60 * 1000);
    return () => window.clearInterval(id);
  }, [visible, pollingEnabled, session.agentId, load]);

  const openDetail = (lead: DemoLead) => {
    navigate(`${process.env.PUBLIC_URL}/DetailViewOps`, {
      state: {
        lead,
        activeTab: "myleads",
        viewName: "myleads",
        recordId: lead.recordId != null ? String(lead.recordId) : undefined,
        agentId: session.agentId,
      },
    });
  };

  const saveRow = async (
    before: DemoLead,
    after: DemoLead,
    touched: LeadSaveTouched,
    _vn: string
  ) => {
    const agentIdSave = resolveUpdateAgentId(after, "myleads", session.agentId);
    await persistLeadWithAudit(before, after, {
      agentIdSave,
      displayName: session.displayName || "Agent",
      sessionAgentId: session.agentId,
      touched,
    });
    await load();
  };

  return (
    <Card
      title="My Leads"
      actions={
        <Button
          size="sm"
          variant="secondary"
          icon={<FilterIcon size={16} aria-hidden />}
          onClick={() => setFilterOpen(true)}
        >
          Filter
        </Button>
      }
    >
      <div className="ds-stack demo-ops-panel">
        <Pager
          summary={`Showing page ${page + 1} of ${totalPages}`}
          canPrev={page > 0}
          canNext={page + 1 < totalPages}
          onPrev={() => setPage((p) => Math.max(0, p - 1))}
          onNext={() => setPage((p) => (p + 1 < totalPages ? p + 1 : p))}
        />

        <LeadsTable
          viewName="myleads"
          leads={leads}
          loading={loading}
          hideIndiaMobile
          onRowOpenDetail={openDetail}
          onSaveRow={saveRow}
        />
      </div>

      <FilterModal
        isOpen={filterOpen}
        toggle={() => setFilterOpen(false)}
        initialFilterOn={filterOn}
        initialFilter={filterStr}
        onApply={(filter, fo) => {
          setFilterStr(filter);
          setFilterOn(fo);
          setPage(0);
        }}
      />
    </Card>
  );
};

const AllLeadsTabBody: React.FC<{ visible: boolean }> = ({ visible }) => {
  const navigate = useNavigate();
  const session = getDemoOpsSession();

  const [preset, setPreset] = useState<PresetKey>("week");
  const [fromDate, setFromDate] = useState(ymd(moment().subtract(6, "days")));
  const [toDate, setToDate] = useState(ymd(moment()));
  const [page, setPage] = useState(0);

  const [leads, setLeads] = useState<DemoLead[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState<number | null>(null);

  const [filterOpen, setFilterOpen] = useState(false);
  const [filterStr, setFilterStr] = useState("");
  const [filterOn, setFilterOn] = useState<FilterOnField>("");
  const [agentFilter, setAgentFilter] = useState("");
  const [agentOptions, setAgentOptions] = useState<
    { value: string; label: string }[]
  >([]);

  useEffect(() => {
    if (!visible || agentOptions.length > 0) return;
    void (async () => {
      try {
        const agents = await getDemoOpsAgents();
        const byId = new Map<string, string>();
        agents.forEach((a) => {
          if (a.agentId != null && a.agentName)
            byId.set(String(a.agentId), a.agentName);
        });
        setAgentOptions(
          Array.from(byId, ([value, label]) => ({ value, label })).sort((a, b) =>
            a.label.localeCompare(b.label)
          )
        );
      } catch (e) {
        console.error(e);
      }
    })();
  }, [visible, agentOptions.length]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await loadDemoOpsLeadsPage({
        fromDate,
        toDate,
        page: agentFilter ? 0 : page,
        // One agent's leads load in a single page so the class-time sort covers all of them.
        size: agentFilter ? AGENT_FILTER_PAGE_SIZE : PAGE_SIZE,
        filter: filterStr,
        filterOn,
        ...(agentFilter ? { agentId: agentFilter } : {}),
      });
      const list = data.content ?? [];
      setLeads(list);
      setTotalPages(Math.max(1, data.totalPages ?? 1));
      if (typeof data.totalElements === "number")
        setTotalElements(data.totalElements);
    } catch (e) {
      console.error(e);
      toast.error("Could not load All Leads.");
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, page, filterStr, filterOn, agentFilter]);

  useEffect(() => {
    if (!visible) return;
    void load();
  }, [visible, load]);

  const setToday = () => {
    const t = ymd(moment());
    setFromDate(t);
    setToDate(t);
    setPreset("today");
    setPage(0);
  };

  const setWeek = () => {
    setFromDate(ymd(moment().subtract(6, "days")));
    setToDate(ymd(moment()));
    setPreset("week");
    setPage(0);
  };

  const openDetail = (lead: DemoLead) => {
    navigate(`${process.env.PUBLIC_URL}/DetailViewOps`, {
      state: {
        lead,
        activeTab: "allleads",
        viewName: "allleads",
        recordId: lead.recordId != null ? String(lead.recordId) : undefined,
        agentId:
          lead.agentId != null ? String(lead.agentId) : session.agentId,
        fromDate,
        toDate,
      },
    });
  };

  const saveRow = async (
    before: DemoLead,
    after: DemoLead,
    touched: LeadSaveTouched,
    _vn: string
  ) => {
    const agentIdSave = resolveUpdateAgentId(after, "allleads", session.agentId);
    await persistLeadWithAudit(before, after, {
      agentIdSave,
      displayName: session.displayName || "Agent",
      sessionAgentId: session.agentId,
      touched,
    });
    await load();
  };

  const downloadCsv = async () => {
    try {
      toast.info("Downloading…");
      const rows: DemoLead[] = [];
      let p = 0;
      let pages = 1;
      while (p < pages) {
        const res = await loadDemoOpsLeadsPage({
          fromDate,
          toDate,
          page: p,
          size: 200,
          filter: filterStr,
          filterOn,
          ...(agentFilter ? { agentId: agentFilter } : {}),
        });
        const chunk = res.content ?? [];
        rows.push(...chunk);
        pages = Math.max(1, res.totalPages ?? 1);
        p++;
        if (chunk.length === 0) break;
      }

      // Same as table: exclude Asia/Kolkata (or Calcutta) + +91 domestic leads.
      const exportRows = rows.filter(
        (l) => !(isAsiaKolkataTimezone(l) && isIndiaCountryCode(l))
      );

      const headers = [
        "recordId",
        "studentId",
        "agentId",
        "agentName",
        "studentName",
        "ageGroup",
        "teacher",
        "courseName",
        "studentTimezone",
        "mobile",
        "classTimeStudentTz",
        "classTime",
        "demoStatus",
        "demoCallStatus1",
        "demoCallStatus2",
      ];
      const lines = [
        headers.join(","),
        ...exportRows.map((l) =>
          [
            l.recordId,
            l.studentId,
            l.agentId,
            l.agentName ?? "",
            leadDisplayName(l),
            l.ageGroup,
            teacherDisplayName(l),
            l.courseName,
            l.studentTimezone,
            `${l.countryCode ?? ""}${l.mobileNumber ?? ""}`,
            l.classDateInTimeZone,
            `${l.slotDate ?? ""} ${l.startTime ?? ""}`.trim(),
            getDisplayDemoStatus(l),
            l.demoCallStatus1,
            l.demoCallStatus2,
          ]
            .map(escapeCsv)
            .join(",")
        ),
      ];
      const blob = new Blob([lines.join("\n")], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `demo-ops-all-leads-${fromDate}_${toDate}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV ready.");
    } catch {
      toast.error("CSV download failed.");
    }
  };

  return (
    <Card
      title="All Leads"
      actions={
        <div className="demo-ops-card-actions">
          <Button
            size="sm"
            variant="secondary"
            icon={<Download size={16} aria-hidden />}
            onClick={() => void downloadCsv()}
          >
            Download CSV
          </Button>
          <Button
            size="sm"
            variant="secondary"
            icon={<FilterIcon size={16} aria-hidden />}
            onClick={() => setFilterOpen(true)}
          >
            Filter
          </Button>
        </div>
      }
    >
      <div className="ds-stack demo-ops-panel">
        <Toolbar>
          <div className="demo-ops-presets" role="group" aria-label="Date range">
            <Button
              size="sm"
              variant="secondary"
              className="demo-ops-preset"
              aria-pressed={preset === "today"}
              onClick={setToday}
            >
              Today
            </Button>
            <Button
              size="sm"
              variant="secondary"
              className="demo-ops-preset"
              aria-pressed={preset === "week"}
              onClick={setWeek}
            >
              Last 7 days
            </Button>
          </div>
          <Field label="Custom from">
            {(ctl) => (
              <TextInput
                {...ctl}
                type="date"
                size="sm"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setPreset("custom");
                  setPage(0);
                }}
              />
            )}
          </Field>
          <Field label="to">
            {(ctl) => (
              <TextInput
                {...ctl}
                type="date"
                size="sm"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setPreset("custom");
                  setPage(0);
                }}
              />
            )}
          </Field>
          <Field label="Agent">
            {(ctl) => (
              <Select
                {...ctl}
                size="sm"
                value={agentFilter}
                onChange={(e) => {
                  setAgentFilter(e.target.value);
                  setPage(0);
                }}
                options={[{ value: "", label: "All agents" }, ...agentOptions]}
              />
            )}
          </Field>
        </Toolbar>

        <Pager
          summary={`Page ${page + 1} of ${totalPages}${
            totalElements != null ? ` · ${totalElements} rows` : ""
          }`}
          canPrev={page > 0}
          canNext={page + 1 < totalPages}
          onPrev={() => setPage((p) => Math.max(0, p - 1))}
          onNext={() => setPage((p) => (p + 1 < totalPages ? p + 1 : p))}
        />

        <LeadsTable
          viewName="allleads"
          leads={leads}
          loading={loading}
          hideIndiaMobile
          onRowOpenDetail={openDetail}
          onSaveRow={saveRow}
        />
      </div>

      <FilterModal
        isOpen={filterOpen}
        toggle={() => setFilterOpen(false)}
        initialFilterOn={filterOn}
        initialFilter={filterStr}
        onApply={(filter, fo) => {
          setFilterStr(filter);
          setFilterOn(fo);
          setPage(0);
        }}
      />
    </Card>
  );
};

const LiveDemoTabBody: React.FC<{ visible: boolean }> = ({ visible }) => {
  const navigate = useNavigate();
  const session = getDemoOpsSession();

  const [leads, setLeads] = useState<DemoLead[]>([]);
  const [countryFilter, setCountryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<LiveDemoStatusFilter>("");
  const [aeFilter, setAeFilter] = useState<LiveDemoAeFilter>("");
  const [tzFilter, setTzFilter] = useState("");
  const [sortDir, setSortDir] = useState<LiveDemoSortDir>("desc");

  const load = useCallback(async () => {
    try {
      const data = await loadDemoOpsLeadsPage({
        page: 0,
        size: 500,
        type: "liveDemo",
        fromDate: "",
      });
      setLeads(data.content ?? []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    if (!visible) return undefined;
    void load();
    const poll = window.setInterval(() => {
      void load();
    }, 5000);
    return () => window.clearInterval(poll);
  }, [visible, load]);

  const uniqueCountries = useMemo(() => {
    const s = new Set<string>();
    for (const l of leads) {
      if (l.country) s.add(String(l.country));
    }
    return Array.from(s.values()).sort();
  }, [leads]);

  const uniqueTz = useMemo(() => {
    const s = new Set<string>();
    for (const l of leads) {
      if (l.studentTimezone) s.add(String(l.studentTimezone));
    }
    return Array.from(s.values()).sort();
  }, [leads]);

  const filteredLeads = useMemo(() => {
    const filters: LiveDemoFilters = {
      country: countryFilter,
      status: statusFilter,
      aeFound: aeFilter,
      timezone: tzFilter,
    };
    return filterLiveDemoLeads(leads, filters, sortDir);
  }, [leads, countryFilter, statusFilter, aeFilter, tzFilter, sortDir]);

  const openDetail = (lead: DemoLead) => {
    navigate(`${process.env.PUBLIC_URL}/DetailViewOps`, {
      state: {
        lead,
        activeTab: "livedemo",
        viewName: "livedemo",
        recordId: lead.recordId != null ? String(lead.recordId) : undefined,
        agentId: session.agentId,
      },
    });
  };

  return (
    <Card title="Live Demo" subtitle="Refreshes every 5 seconds.">
      <div className="ds-stack demo-ops-panel">
        <Toolbar>
          <Field label="Country">
            {(ctl) => (
              <Select
                {...ctl}
                size="sm"
                value={countryFilter}
                onChange={(e) => setCountryFilter(e.target.value)}
                options={[
                  { value: "", label: "All countries" },
                  ...uniqueCountries.map((country) => ({ value: country, label: country })),
                ]}
              />
            )}
          </Field>
          <Field label="Demo status">
            {(ctl) => (
              <Select
                {...ctl}
                size="sm"
                value={statusFilter}
                onChange={(e) =>
                  setStatusFilter(e.target.value as LiveDemoStatusFilter)
                }
                options={LIVE_DEMO_STATUS_OPTIONS.map((opt) => ({
                  value: opt.value,
                  label: opt.label,
                }))}
              />
            )}
          </Field>
          <Field label="AE assigned">
            {(ctl) => (
              <Select
                {...ctl}
                size="sm"
                value={aeFilter}
                onChange={(e) => setAeFilter(e.target.value as LiveDemoAeFilter)}
                options={[
                  { value: "", label: "All" },
                  { value: "AE_FOUND", label: "AE assigned" },
                  { value: "NO_AE", label: "No AE" },
                ]}
              />
            )}
          </Field>
          <Field label="Student timezone">
            {(ctl) => (
              <Select
                {...ctl}
                size="sm"
                value={tzFilter}
                onChange={(e) => setTzFilter(e.target.value)}
                options={[
                  { value: "", label: "All zones" },
                  ...uniqueTz.map((tz) => ({ value: tz, label: tz })),
                ]}
              />
            )}
          </Field>
        </Toolbar>

        <LiveDemoTable
          leads={filteredLeads}
          onRowOpenDetail={openDetail}
          sortDir={sortDir}
          onToggleSort={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
        />
      </div>
    </Card>
  );
};

export const DemoOpsHub: React.FC = () => {
  const location = useLocation();
  const session = useMemo(() => getDemoOpsSession(), []);
  // Emails on "LiveDemoAccessOnly" whose role has no Demo Ops access of its own get the Live Demo tab only
  useEmailAccessConfig();
  const liveDemoOnly =
    hasEmailAccess("LiveDemoAccessOnly") && (isSalesRestrictedUser() || isOutSourcingUser());

  const [tab, setTab] = useState<DemoOpsTabKey>(() => {
    const s = tabFromUnknown(
      (location.state as Record<string, unknown> | undefined)?.activeTab
    );
    return s ?? "allleads";
  });

  useEffect(() => {
    const s = tabFromUnknown(
      (location.state as Record<string, unknown> | undefined)?.activeTab
    );
    if (s) setTab(s);
  }, [location.state]);

  useEffect(() => {
    if (liveDemoOnly) setTab("livedemo");
  }, [liveDemoOnly]);

  const [agentOnline, setAgentOnline] = useState<boolean | null>(null);
  const [toggleBusy, setToggleBusy] = useState(false);
  const [timingOptions, setTimingOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [timing, setTiming] = useState("");
  const [shiftRows, setShiftRows] = useState<ShiftRow[]>([]);

  const pollActive = agentOnline === true;
  // agentOnline stays null until the first status load finishes
  const noTimings = agentOnline !== null && timingOptions.length === 0;

  // Shift rows for this user are stored under a different agent id than the login's uId
  const availabilityAgentId = useMemo(() => {
    try {
      const email = JSON.parse(localStorage.getItem("userProfileData") || "{}").emailId;
      if (String(email).toLowerCase() === "pragya@artiumacademy.com") return "58737";
    } catch {
      /* ignore */
    }
    return session.agentId;
  }, [session.agentId]);

  const loadAgent = useCallback(async () => {
    if (!availabilityAgentId) return;
    try {
      const { status, timings } = await getDemoOpsAgentStatus(availabilityAgentId);
      setAgentOnline(status === "active");
      const rows: ShiftRow[] = timings.map((r) => ({
        id: String(r.id),
        status: String(r.status ?? "").toLowerCase(),
        day: String(r.day ?? ""),
        start: String(r.shiftStartTime ?? ""),
        end: String(r.shiftEndTime ?? ""),
      }));
      setShiftRows(rows);
      const todays = rows
        .filter((r) => r.day === moment().format("dddd"))
        .sort((x, y) => x.start.localeCompare(y.start));
      setTimingOptions(
        todays.map((r) => {
          const join = findNextDayJoin(rows, r);
          return {
            value: r.id,
            label:
              `${r.status === "active" ? "✓ " : ""}${hm(r.start)} - ${hm(r.end)}` +
              (join ? ` (+ ${join.day.slice(0, 3)} ${hm(join.start)} - ${hm(join.end)})` : ""),
          };
        })
      );
      const activeToday = todays.find((r) => r.status === "active");
      if (activeToday) setTiming(activeToday.id);
    } catch {
      trackEvent(MixpanelEvents.DEMOOPS_AGENT_STATUS_API_FAILURE);
      setAgentOnline(false);
      toast.warning("Unable to read agent availability.");
    }
  }, [availabilityAgentId]);

  useEffect(() => {
    void loadAgent();
  }, [loadAgent]);

  // Activates the chosen timing (plus a contiguous next-day slot, if any); every other row goes inactive
  const activateShift = async (shiftId: string) => {
    if (!availabilityAgentId) return;
    const chosen = shiftRows.find((r) => r.id === shiftId);
    const join = chosen ? findNextDayJoin(shiftRows, chosen) : undefined;
    const keep = [shiftId, join?.id].filter((x): x is string => !!x);
    for (const r of shiftRows) {
      if (r.status === "active" && !keep.includes(r.id)) {
        await postDemoOpsAgentShiftStatus(availabilityAgentId, "inactive", r.id);
      }
    }
    for (const id of keep) {
      await postDemoOpsAgentShiftStatus(availabilityAgentId, "active", id);
    }
  };

  const onTimingChange = async (shiftId: string) => {
    setTiming(shiftId);
    if (!shiftId || !agentOnline || toggleBusy) return;
    setToggleBusy(true);
    try {
      await activateShift(shiftId);
      toast.success("Timing updated.");
    } catch {
      trackEvent(MixpanelEvents.DEMOOPS_AGENT_STATUS_TOGGLE_ERROR);
      toast.error("Could not update agent timing.");
    } finally {
      await loadAgent();
      setToggleBusy(false);
    }
  };

  const toggleAgent = async () => {
    if (!availabilityAgentId || agentOnline === null) return;
    const nextStatus: "active" | "inactive" = agentOnline ? "inactive" : "active";
    if (nextStatus === "active" && !timing) {
      toast.warning("Select a shift timing before marking yourself active.");
      return;
    }
    setToggleBusy(true);
    try {
      if (nextStatus === "active") {
        await activateShift(timing);
      } else {
        await postDemoOpsAgentAvailability(availabilityAgentId, nextStatus);
      }
      toast.success(nextStatus === "active" ? "Marked active." : "Marked inactive.");
      await loadAgent();
    } catch {
      trackEvent(MixpanelEvents.DEMOOPS_AGENT_STATUS_TOGGLE_ERROR);
      toast.error("Could not update agent status.");
    } finally {
      setToggleBusy(false);
    }
  };

  return (
    <Page className="demo-ops">
      <PageHeader
        eyebrow="Demo Ops"
        title="Demo Operations"
        subtitle="Work your demo leads, log call outcomes and watch demos that are live right now."
        actions={
          <div className="demo-ops-availability">
            <Badge tone={agentOnline ? "success" : "danger"}>
              {agentOnline ? "Active" : "Inactive"}
            </Badge>
            <div className="form-check form-switch">
              <Input
                className="form-check-input"
                type="checkbox"
                role="switch"
                title={!agentOnline && !timing ? "Select a timing first" : "Availability"}
                aria-label="Availability"
                disabled={toggleBusy || !availabilityAgentId || (!agentOnline && !timing)}
                checked={!!agentOnline}
                onChange={() => void toggleAgent()}
              />
            </div>
            <Input
              type="select"
              bsSize="sm"
              title="Active timings"
              className="demo-ops-timing-select"
              value={timing}
              disabled={
                toggleBusy ||
                !availabilityAgentId ||
                agentOnline === true ||
                noTimings
              }
              onChange={(e) => void onTimingChange(e.target.value)}
            >
              <option value="">
                {noTimings ? "No available shift timings" : "Select timing"}
              </option>
              {timingOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Input>
          </div>
        }
      />

      <Tabs<DemoOpsTabKey>
        label="Demo Ops views"
        items={
          liveDemoOnly
            ? [{ key: "livedemo", label: "Live Demo" }]
            : [
                { key: "myleads", label: "My Leads" },
                { key: "allleads", label: "All Leads" },
                { key: "livedemo", label: "Live Demo" },
              ]
        }
        active={liveDemoOnly ? "livedemo" : tab}
        onChange={setTab}
      />

      {!liveDemoOnly && (
        <div role="tabpanel" hidden={tab !== "myleads"}>
          <MyLeadsTabBody
            visible={tab === "myleads"}
            pollingEnabled={pollActive && tab === "myleads"}
          />
        </div>
      )}
      {!liveDemoOnly && (
        <div role="tabpanel" hidden={tab !== "allleads"}>
          <AllLeadsTabBody visible={tab === "allleads"} />
        </div>
      )}
      <div role="tabpanel" hidden={!liveDemoOnly && tab !== "livedemo"}>
        <LiveDemoTabBody visible={liveDemoOnly || tab === "livedemo"} />
      </div>
    </Page>
  );
};

export default DemoOpsHub;
